import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const DeleteBranchModal = ({ open, onClose, onConfirm, branches = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  const [deleteOption, setDeleteOption] = useState('delete-all');
  const [selectedBranch, setSelectedBranch] = useState('');

  // Get all branch IDs except 'start' and create display names
  const branchOptions = Object.keys(branches)
    .filter(branchId => branchId !== 'start')
    .map(branchId => {
      const branch = branches[branchId] || [];
      let displayName = branchId;
      
      // For option branches, try to get the option title from the first node (trigger)
      if (branchId.startsWith('option-') && branch.length > 0) {
        const firstNode = branch[0];
        if (firstNode && firstNode.trigger && firstNode.trigger.config) {
          // Look for the variable field that contains the option title
          for (const config of firstNode.trigger.config) {
            if (config.fields) {
              for (const field of config.fields) {
                if (field.type === 'variable' && field.selectedValue) {
                  displayName = field.selectedValue;
                  break;
                }
              }
            }
          }
        }
      }
      
      // For no-reply branches, use a descriptive name
      if (branchId.startsWith('no-reply-')) {
        displayName = 'No Reply';
      }
      
      return {
        id: branchId,
        name: displayName
      };
    });

  const handleConfirm = () => {
    onConfirm({
      deleteOption,
      selectedBranch: deleteOption === 'save-specific' ? selectedBranch : null
    });
    onClose();
  };

  const handleClose = () => {
    setDeleteOption('delete-all');
    setSelectedBranch('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: isDark ? '#23272b' : '#fff',
          border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
        }
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 600,
          color: isDark ? '#fff' : '#222',
          pb: 1
        }}
      >
        Delete branch action
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={deleteOption}
            onChange={(e) => setDeleteOption(e.target.value)}
          >
            <FormControlLabel
              value="delete-all"
              control={<Radio />}
              label={
                <Typography sx={{ color: isDark ? '#fff' : '#222', fontSize: 14 }}>
                  Delete all actions below this branch
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            
            <FormControlLabel
              value="save-specific"
              control={<Radio />}
              label={
                <Typography sx={{ color: isDark ? '#fff' : '#222', fontSize: 14 }}>
                  Save all actions from a specific branch
                </Typography>
              }
              sx={{ mb: 2 }}
            />
          </RadioGroup>
        </FormControl>

        {deleteOption === 'save-specific' && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel 
                sx={{ 
                  color: isDark ? '#bbb' : '#666',
                  '&.Mui-focused': {
                    color: theme.palette.primary.main
                  }
                }}
              >
                Select branch to save
              </InputLabel>
                             <Select
                 value={selectedBranch}
                 onChange={(e) => setSelectedBranch(e.target.value)}
                 sx={{
                   bgcolor: isDark ? '#2e3440' : '#f5f5f5',
                   color: isDark ? '#fff' : '#222',
                   '& .MuiOutlinedInput-notchedOutline': {
                     borderColor: isDark ? '#555' : '#ccc'
                   },
                   '&:hover .MuiOutlinedInput-notchedOutline': {
                     borderColor: theme.palette.primary.main
                   },
                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                     borderColor: theme.palette.primary.main
                   }
                 }}
               >
                 {branchOptions.map((branch) => (
                   <MenuItem key={branch.id} value={branch.id}>
                     {branch.name}
                   </MenuItem>
                 ))}
               </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{
            color: isDark ? '#bbb' : '#666',
            '&:hover': {
              bgcolor: isDark ? '#2e3440' : '#f0f0f0'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={deleteOption === 'save-specific' && !selectedBranch}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: '#fff',
            '&:hover': {
              bgcolor: theme.palette.primary.dark
            },
            '&:disabled': {
              bgcolor: isDark ? '#555' : '#ccc',
              color: isDark ? '#888' : '#999'
            }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteBranchModal;
